import { useState, useEffect, useCallback } from 'react'
import { useLocation, Link } from 'react-router-dom'
import {
  AdminPortalFirms,
  GoldenText,
  AdminFirmViewContainer,
  OrangeText,
  AdminFirmViewSectionTitle,
  FlexDiv,
  OrangeButton,
  OrangeButtonSimpleText
} from 'src/components/portal/default'
import PillButton from 'src/components/basic/PillButton'
import FirmSearchInput from 'src/components/portal/default/FirmSearchInput'
import FirmViewHeader from 'src/components/portal/default/FirmViewComponents/FirmViewHeader'
import FirmViewApplication from 'src/components/portal/default/FirmViewComponents/FirmViewApplication'
import FirmViewContactInformation from 'src/components/portal/default/FirmViewComponents/FirmViewContactInformation'
import FirmViewAddresses from 'src/components/portal/default/FirmViewComponents/FirmViewAddresses'
import FirmViewQuote from 'src/components/portal/default/FirmViewComponents/FirmViewQuote'
import FirmViewPolicy from 'src/components/portal/default/FirmViewComponents/FirmViewPolicy'
import FirmViewInvoices from 'src/components/portal/default/FirmViewComponents/FirmViewInvoices'
import Pagination from 'src/components/basic/Pagination'
import {
  FirmSearchOption,
  FirmSearchListData,
  DefaultSearchData,
  FirmDetailsData,
  FirmDetailsInvoiceData,
  FirmDetailsPolicyData,
  FirmDetailsQuoteData,
  FirmDetailsUpdateAddressInputs,
  FirmDetailsUpdateContactsInputs,
  FirmDetailsContactData
} from 'src/dtos/portal'
import { RenewalData } from 'src/dtos/renewalData'
import FirmListRow from 'src/components/portal/default/FirmListRow'
import { HiOutlineArrowNarrowLeft } from 'react-icons/hi'
import PortalRightDrawer from 'src/components/basic/PortalRightDrawer'
import DrawerHeader from 'src/components/portal/default/AppListDrawerHeader'
import AppListDrawerContent from 'src/components/portal/default/AppListDrawerContent'
import { api } from 'src/services/apiClient'
import { firmSearchOptionsToRequest } from 'src/constants/portal'
import { format } from 'date-fns'
import Modal from 'react-modal'
import loadingImg from 'src/assets/loader-green.svg'
import { AxiosError } from 'axios'

const Firms = () => {
  const { search, pathname } = useLocation()
  const [inputValue, setInputValue] = useState('')
  const [selectedOption, setSelectedOption] = useState<FirmSearchOption>('FIRM_NUMBER')
  const [noMatchesFound, setNoMatchesFound] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [paginationData, setPaginationData] = useState<DefaultSearchData>({
    currentPage: 1,
    totalCount: 0,
    totalPages: 0
  })
  const [firmsList, setFirmsList] = useState<FirmSearchListData[]>([])
  const [applicationDrawerOpen, setApplicationDrawerOpen] = useState(false)
  const [firmDetails, setFirmDetails] = useState<FirmDetailsData>({} as FirmDetailsData)
  const [firmDetailsInvoice, setFirmDetailsInvoice] = useState<FirmDetailsInvoiceData | undefined>(undefined)
  const [firmDetailsPolicy, setFirmDetailsPolicy] = useState<FirmDetailsPolicyData | undefined>(undefined)
  const [firmDetailsQuote, setFirmDetailsQuote] = useState<FirmDetailsQuoteData | undefined>(undefined)
  const [removeAddressModalOpen, setRemoveAddressModalOpen] = useState(false)
  const [addressModalBtnsDisabled, setAddressModalBtnsDisabled] = useState(false)
  const [drawerApplication, setDrawerApplication] = useState<RenewalData | undefined>(undefined)
  const [primaryContactData, setPrimaryContactData] = useState<FirmDetailsContactData | undefined>(undefined)
  const { view, action } = Object.fromEntries(new URLSearchParams(search))

  const fetchFirmDetails = useCallback(async () => {
    if (view !== undefined) {
      const firmId = Number(view)
      if (!isNaN(firmId) && firmId > 0) {
        try {
          const { data } = await api.get(`/Firm/${firmId}`)

          setFirmDetails(data)
        } catch (e) {
          setFirmDetails({} as FirmDetailsData)
        }
      } else {
        setFirmDetails({} as FirmDetailsData)
      }
    } else {
      setFirmDetails({} as FirmDetailsData)
    }
  }, [view])

  const fetchFirmInvoiceInfo = useCallback(async () => {
    if (view !== undefined && view !== '0') {
      try {
        const { data } = await api.get(`/Firm/GetPolicyBilling/${view}`)

        setFirmDetailsInvoice(data)
      } catch (e) {
        setFirmDetailsInvoice(undefined)
      }
    } else {
      setFirmDetailsInvoice(undefined)
    }
  }, [view])

  const fetchPolicyDetails = useCallback(async () => {
    if (view !== undefined && view !== '0') {
      try {
        const { data } = await api.get(`/Firm/GetPolicyDetails/${view}`)

        setFirmDetailsPolicy(data)
      } catch (e) {
        setFirmDetailsPolicy(undefined)
      }
    } else {
      setFirmDetailsPolicy(undefined)
    }
  }, [view])

  const fetchQuoteDetails = useCallback(async () => {
    if (view !== undefined && view !== '0') {
      try {
        const { data } = await api.get(`/Firm/GetQuoteDetails/${view}`)

        setFirmDetailsQuote(data)
      } catch (e) {
        setFirmDetailsQuote(undefined)
      }
    } else {
      setFirmDetailsQuote(undefined)
    }
  }, [view])

  useEffect(() => {
    if(action && action === '1') {
      setApplicationDrawerOpen(true)
    }
  }, [action])

  useEffect(() => {
    fetchFirmDetails()
  }, [fetchFirmDetails])

  useEffect(() => {
    fetchFirmInvoiceInfo()
  }, [fetchFirmInvoiceInfo])

  useEffect(() => {
    fetchPolicyDetails()
  }, [fetchPolicyDetails])

  useEffect(() => {
    fetchQuoteDetails()
  }, [fetchQuoteDetails])


  const handleFetchFirms = useCallback(async (page?: number) => {
    try {
      const { data } = await api.get('/Firm/search', {
        params: {
          PageNumber: page ?? 1,
          PageSize: 5,
          [firmSearchOptionsToRequest[selectedOption]]: inputValue
        }
      })

      const { firms, ...pagination } = data

      if (page) {
        setCurrentPage(page)
      } else {
        setCurrentPage(1)
      }
      setPaginationData(pagination)
      if (firms.length === 0) {
        setNoMatchesFound(true)
        setFirmsList([])
      } else {
        setNoMatchesFound(false)
        setFirmsList(firms)
      }
    } catch (e) {
      setNoMatchesFound(true)
    }
  }, [inputValue, selectedOption])

  const handleChangePage = (page: number) => {
    handleFetchFirms(page)
  }

  const handleDownloadClick = async () => {
    if (firmDetails?.applicationId && firmDetails?.primaryContact?.firstName && firmDetails?.primaryContact?.lastName) {
      try {
        const { data } = await api.get(`/Applications/download/${firmDetails?.applicationId}`, { responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `${[(firmDetails?.primaryContact?.firstName ?? ''), (firmDetails?.primaryContact?.lastName ?? '')].join(' ')}_Application.json`
        )
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (e) { }
    }
  }

  const handleSaveAddressChange = async (addressInputs: FirmDetailsUpdateAddressInputs) => {
    try {
      await api.put('/Firm/address', { ...addressInputs })
      await fetchFirmDetails()
    } catch (error) {
      if (error instanceof AxiosError) {
        // Log actual error details to browser console
        if(error.response?.data?.errorDetails) {
          console.error(error.response.data.errorDetails)
        }
        throw new Error(error.response?.data?.errorMessage || 'An error occurred while saving address.')
      }
      throw error
    }
  }

  const handleSaveFirmContact = async (contactInputs: FirmDetailsUpdateContactsInputs) => {
    try {
      await api.put(`/Firm/contactinfo/${view}`, { ...contactInputs })
      await fetchFirmDetails()
    } catch (e) {}
  }

  const handleRemoveBillingAddress = async () => {
    setAddressModalBtnsDisabled(true)
    try {
      await api.post('/Firm/RemoveFirmAddress', {}, {
        params: {
          firmId: view,
          type: 1
        }
      })
      await fetchFirmDetails()
      setRemoveAddressModalOpen(false)
    } catch (e) { } finally {
      setAddressModalBtnsDisabled(false)
    }
  }

  return (firmDetails.applicationId !== null && firmDetails.applicationId !== undefined && firmDetails.applicationId >= 0) ? (
    <AdminFirmViewContainer>
      <Link to={pathname} style={{ textDecoration: 'none' }}>
        <OrangeText style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <HiOutlineArrowNarrowLeft />
          Back to search results
        </OrangeText>
      </Link>
      <FlexDiv margin="48px 0 64px">
        <FirmViewHeader
          firmName={(firmDetails?.name ?? '')}
          firmNumber={firmDetails?.number ?? ''}
          email={firmDetails?.primaryContact?.emailAddress ?? ''}
        />
      </FlexDiv>
      <AdminFirmViewSectionTitle style={{ marginBottom: '24px' }}>
        FIRM INFORMATION
      </AdminFirmViewSectionTitle>
      <FlexDiv dir='column' gap="48px">
        {firmDetails.applicationId > 0 ? (
          <FirmViewApplication
            onViewClick={() => setApplicationDrawerOpen(true)}
            dateSubmitted={firmDetails?.dateSubmitted}
            onDownloadClick={handleDownloadClick}
          />
        ) : null}
        <FirmViewContactInformation
          firmId={view}
          isAdmin = {true}
        />
        <FirmViewAddresses
          addresses={firmDetails}
          firmId={Number(view)}
          saveAddress={handleSaveAddressChange}
          removeBillingAddress={() => setRemoveAddressModalOpen(true)}
          saveContact={handleSaveFirmContact}
        />
      </FlexDiv>
      {firmDetailsQuote ? (
        <>
          <AdminFirmViewSectionTitle style={{ margin: '64px 0 24px' }}>
            QUOTE
          </AdminFirmViewSectionTitle>
          <FirmViewQuote
            firmDetailsQuote={firmDetailsQuote}
            firmId={+view}
          />
        </>
      ) : null}
      {firmDetailsPolicy ? (
        <>
          <AdminFirmViewSectionTitle style={{ margin: '64px 0 24px' }}>
            POLICY
          </AdminFirmViewSectionTitle>
          <FirmViewPolicy policyData={firmDetailsPolicy} firmId={Number(view)} />
        </>
      ) : null}
      {firmDetailsInvoice ? (
        <>
          <AdminFirmViewSectionTitle style={{ margin: '64px 0 24px' }}>
            INVOICES
          </AdminFirmViewSectionTitle>
          <FirmViewInvoices invoiceData={firmDetailsInvoice} />
        </>
      ) : null}
      {firmDetails.applicationId > 0 ? (
        <PortalRightDrawer
          open={applicationDrawerOpen}
          toggle={() => setApplicationDrawerOpen(false)}
          header={
            <DrawerHeader
              applicationData={{
                applicantName: [(firmDetails?.primaryContact?.firstName ?? ''), (firmDetails?.primaryContact?.lastName ?? '')].join(' '),
                dateCompleted: firmDetails?.dateCompleted ? format(new Date(firmDetails.dateCompleted), 'MM/dd/yyyy') : '',
                dateSubmitted: firmDetails?.dateSubmitted ? format(new Date(firmDetails.dateSubmitted), 'MM/dd/yyyy') : '',
                id: firmDetails?.applicationId
              }}
              submitApplication={async () => { }}
              sendReminder={async () => { }}
              rejectApplication={async () => { }}
              sendMagicLink={async () => { }}
              deleteApplication={async () => { }}
              isNewBusiness={true}
              submitHidden={false}
              applicationLoaded={true}
            />
          }
        >
          <AppListDrawerContent
            applicationId={firmDetails?.applicationId}
            isNewBusiness={true}
            isJudge={false}
            application={drawerApplication}
            setApplication={setDrawerApplication}
            primaryContactData = {primaryContactData}
            setPrimaryContactData = {setPrimaryContactData}
          />
        </PortalRightDrawer>
      ) : null}
      <Modal
        isOpen={removeAddressModalOpen}
        onRequestClose={() => setRemoveAddressModalOpen(false)}
        overlayClassName="react-modal-overlay"
        className="react-modal-content"
      >
        <FlexDiv dir='column' gap="16px">
          <strong>Are you sure you want to remove this billing address?</strong>
          <span>This information will not be saved once it has been removed.</span>
          <FlexDiv dir="column" gap="24px" margin="48px 0 0 0" align='flex-end'>
            <OrangeButton
              onClick={handleRemoveBillingAddress}
              disabled={addressModalBtnsDisabled}
            >
              Remove Billing Address
            </OrangeButton>
            <OrangeButtonSimpleText
              onClick={() => setRemoveAddressModalOpen(false)}
              disabled={addressModalBtnsDisabled}
            >
              Cancel
            </OrangeButtonSimpleText>
          </FlexDiv>
        </FlexDiv>
      </Modal>
    </AdminFirmViewContainer>
  ) : !!view ? (
    <FlexDiv maxWidth='100%' height='100%' align='center' justify='center'>
      <img src={loadingImg} alt="loading..." />
    </FlexDiv>
  ) : (
    <AdminPortalFirms>
      <header>
        <strong>SEARCH BY</strong>
        <div className='pill-buttons-container'>
          <PillButton
            selected={selectedOption === 'FIRM_NUMBER'}
            onClick={() => setSelectedOption('FIRM_NUMBER')}
          >
            Firm Number
          </PillButton>
          <PillButton
            selected={selectedOption === 'FIRM_NAME'}
            onClick={() => setSelectedOption('FIRM_NAME')}
          >
            Firm Name
          </PillButton>
          <PillButton
            selected={selectedOption === 'ADMIN_FIRST_NAME'}
            onClick={() => setSelectedOption('ADMIN_FIRST_NAME')}
          >
            Admin First Name
          </PillButton>
          <PillButton
            selected={selectedOption === 'ADMIN_LAST_NAME'}
            onClick={() => setSelectedOption('ADMIN_LAST_NAME')}
          >
            Admin Last Name
          </PillButton>
          <PillButton
            selected={selectedOption === 'EMAIL'}
            onClick={() => setSelectedOption('EMAIL')}
          >
            Email
          </PillButton>
          <PillButton
            selected={selectedOption === 'POLICY_NUMBER'}
            onClick={() => setSelectedOption('POLICY_NUMBER')}
          >
            Policy Number
          </PillButton>
          <PillButton
            selected={selectedOption === 'QUOTE_NUMBER'}
            onClick={() => setSelectedOption('QUOTE_NUMBER')}
          >
            Quote Number
          </PillButton>
        </div>
        <FirmSearchInput
          onSearchClick={handleFetchFirms}
          placeholder={`Enter ${selectedOption.replaceAll('_', ' ').toLowerCase()}...`}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
        />
      </header>
      <section>
        {noMatchesFound ? (
          <GoldenText style={{ marginTop: '26px' }}>No matching results found.</GoldenText>
        ) : firmsList.length > 0 ? firmsList.map(firm => (
          <FirmListRow key={firm.firmNumber + firm.quoteNumber} rowData={firm} />
        )) : null}
      </section>
      {paginationData.totalPages > 1 ? (
        <footer>
          <Pagination currentPage={currentPage} setCurrentPage={handleChangePage} totalPages={paginationData.totalPages} />
        </footer>
      ) : null}
    </AdminPortalFirms>
  )
}

export default Firms